import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import Lightbox from "@/components/feature/Lightbox";
import EmptyState from "@/components/base/EmptyState";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const inlineImage =
  "https://readdy.ai/api/search-image?query=Stylized%20anime%20illustration%20of%20a%20colorful%20anime%20city%20skyline%20at%20dusk%20with%20glowing%20windows%20and%20passing%20trains%2C%20warm%20orange%20and%20teal%20lighting%2C%20cel%20shaded%20anime%20art%20style%2C%20cinematic%20wide%20composition%2C%20detailed%20background&width=1200&height=675&seq=news-inline-scene&orientation=landscape";

export default function NewsDetail() {
  const { id } = useParams();
  const [zoom, setZoom] = useState<string | null>(null);
  const item = useMemo(() => newsList.find((n) => n.id === id), [id]);

  usePageMeta(
    item ? `${item.title} - 动漫资讯 - 91动漫` : "资讯详情 - 91动漫",
    item ? item.summary : "91动漫资讯详情页。",
    item ? `91动漫,${item.tags.join(",")},动漫资讯` : "91动漫,动漫资讯"
  );

  useEffect(() => {
    setZoom(null);
  }, [id]);

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          title="没有找到这篇资讯"
          description="它可能已被移除，或链接有误。你可以返回资讯列表查看其他内容。"
          actionLabel="返回动漫资讯"
          onAction={() => {
            window.REACT_APP_NAVIGATE("/news");
          }}
          icon="ri-file-unknow-line"
        />
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.id !== item.id && n.tags.some((t) => item.tags.includes(t)))
    .slice(0, 3);

  return (
    <div className="w-full">
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "动漫资讯", to: "/news" }, { label: item.title }]} />
      </div>

      <article className="mx-auto max-w-[900px] px-4 pb-10 md:px-8">
        <div className="flex flex-wrap items-center gap-2">
          {item.tags.map((t) => (
            <Link
              key={t}
              to={`/search?q=${encodeURIComponent(t)}`}
              className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-200"
            >
              #{t}
            </Link>
          ))}
        </div>

        <h1 className="mt-4 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-4xl">
          {item.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-base" />
            {item.date}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line text-base" />
            来源：{item.source}
          </span>
        </div>

        <figure className="mt-6">
          <button
            type="button"
            onClick={() => setZoom(item.cover)}
            className="group relative block w-full cursor-zoom-in overflow-hidden rounded-lg"
          >
            <img
              src={item.cover}
              alt={`${item.title} - 91动漫资讯配图`}
              title={`${item.title} - 91动漫`}
              className="h-[240px] w-full object-top object-cover transition-transform duration-500 group-hover:scale-105 md:h-[420px]"
            />
            <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-secondary-950/70 px-3 py-1 text-xs text-white backdrop-blur-sm">
              <i className="ri-zoom-in-line" />
              点击放大
            </span>
          </button>
          <figcaption className="mt-2 text-center text-xs text-foreground-400">
            配图：{item.title}（点击图片可放大查看）
          </figcaption>
        </figure>

        <div className="mt-8 flex flex-col gap-5">
          {item.content.map((p, i) => (
            <div key={i}>
              <p className="text-[15px] leading-[1.9] text-foreground-700 md:text-base">{p}</p>
              {i === 1 && (
                <figure className="mt-5">
                  <button
                    type="button"
                    onClick={() => setZoom(inlineImage)}
                    className="group block w-full cursor-zoom-in overflow-hidden rounded-lg"
                  >
                    <img
                      src={inlineImage}
                      alt={`${item.title} - 91动漫正文配图`}
                      title="91动漫 动漫资讯配图"
                      className="h-[200px] w-full object-top object-cover transition-transform duration-500 group-hover:scale-105 md:h-[360px]"
                    />
                  </button>
                </figure>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
          >
            <i className="ri-arrow-left-line" />
            返回动漫资讯
          </Link>
          <div className="flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <Link
                key={t}
                to={`/search?q=${encodeURIComponent(t)}`}
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-3 py-2 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line" />
                {t}
              </Link>
            ))}
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="border-t border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
            <div className="flex items-center gap-2">
              <i className="ri-links-line text-xl text-primary-600" />
              <h4 className="font-heading text-xl font-bold text-foreground-950">
                <Link to="/news" className="transition-colors hover:text-primary-700">
                  相关推荐
                </Link>
              </h4>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {related.map((r) => (
                <NewsCard key={r.id} item={r} />
              ))}
            </div>
          </div>
        </section>
      )}

      {zoom && <Lightbox src={zoom} alt={item.title} onClose={() => setZoom(null)} />}
    </div>
  );
}